import React, { useState, useEffect } from "react";
import { Modal, Button, Checkbox, Tooltip, Spinner } from "@tag/tag-components-react-v4";
import { TextField } from "@mui/material";
import moment from "moment";
import CustomerService from "../../services/CustomerService";
import { CustomerDTO } from "../../types/CustomerDTO";
import { PeoplePlannerDisableInfoModel } from "../../types/PeoplePlannerDisableInfoModel";
import {
  DISABLE_OLD_APP_DATE_AND_TIME_TOOLTIP_TEXT,
  DISABLE_OLD_APP_TOOLTIP_TEXT,
} from "../../common/constant";
import { ModuleAlert } from "../../common/ModuleAlert/ModuleAlert";

import styles from "./DisableAppInsideAcrModal.module.scss";
import { handleDisableModalDateTimeChange } from "../../common/utils";


interface DisableAppInsideAcrModalProps {
  visible: boolean;
  selectedCustomer: CustomerDTO;
  handleClose: Function;
  setAlert: Function;
  handleCustomerMenuModalClose: Function;
}

export const DisableAppInsideAcrModal: React.FC<
  DisableAppInsideAcrModalProps
> = ({ visible, selectedCustomer, handleClose, setAlert, handleCustomerMenuModalClose }) => {
  const [isOldPeoplePlannerDisabled, setIsOldPeoplePlannerDisabled] = useState(false);
  const [disableDateTime, setDisableDateTime] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState<string | undefined>();
  const [isDirty, setIsDirty] = useState(false);
  const [alert, setAlertState] = useState<{ message: string; type: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const showAlert = (message: string, type: "success" | "error") => {
    setAlertState({ message, type });
  };

  const handleSave = async () => {
    try {
      const disableInfo: PeoplePlannerDisableInfoModel = {
        isOldPeoplePlanner: true,
        disablePeoplePlanner: isOldPeoplePlannerDisabled,
        disablePeoplePlannerDate: disableDateTime ? disableDateTime : null,
      };

      await CustomerService.updatePeoplePlannerDisableInfo(
        selectedCustomer.clientId,
        disableInfo
      );
      setAlert({type: 'success', message: 'Settings saved successfully'})
      handleClose();
      handleCustomerMenuModalClose();
    } catch (error) {
      setAlert({type: 'error', message: 'Failed to save settings. Please try again'});
      handleCustomerMenuModalClose();
    }
  };

  useEffect(() => {
    setLoading(true);
    const isOldPeoplePlanner = true ;
    CustomerService.getPeoplePlannerDisableInfo(selectedCustomer.clientId, isOldPeoplePlanner).then(
      (data) => {
        setIsOldPeoplePlannerDisabled(data.disablePeoplePlanner);
        if (data.disablePeoplePlannerDate) {
          const date = new Date(data.disablePeoplePlannerDate);
          setDisableDateTime(date);

          const time = moment(date).format("HH:mm");
          setTimeValue(time);
        } else {
          setDisableDateTime(null);
          setTimeValue(undefined);
        }
      }
    );
    setLoading(false);
  }, [selectedCustomer.clientId]);

  if (loading) {
    return <Spinner />;
  }
  
  return (
    <>
    {alert && (
      <ModuleAlert
        message={alert.message}
        type={alert.type as "success" | "error"}
        onClose={() => setAlertState(null)}
      />
    )}
    <Modal
      visible={visible}
      onCancelButtonClick={() => {
        handleClose();
      }}
      accent="teal"
      heading="Remove APP Button from ACR"
      onClose={() => handleClose()}
      primaryButtonProps={{
        text: "Save",
        onClick: handleSave,
        disabled: !isDirty,
      }}
    >
      <div className={styles.modalContent}>
        <Tooltip
          className={styles.tooltip}
          tooltipFor={
            <div className={styles.checkboxContainer}>
              <Checkbox
                device="touch"
                checked={isOldPeoplePlannerDisabled}
                onChange={(value) => {
                  setIsOldPeoplePlannerDisabled(value.checked);
                  setIsDirty(true);
                  if (value.checked) {
                    const currentDateTime = new Date();
                    setDisableDateTime(currentDateTime);

                    const formattedTime = moment(currentDateTime).format("HH:mm");
                    setTimeValue(formattedTime);
                  }
                }}
                disabled={!!disableDateTime}
              />
              <span className={styles.checkboxLabel}>
                Remove APP Button from ACR
              </span>
            </div>
          }
        >
          {DISABLE_OLD_APP_TOOLTIP_TEXT}
        </Tooltip>
        <Tooltip
          className={styles.tooltip}
          tooltipFor={
            <TextField
              id="datetime-local"
              className={styles.dateAndTimePicker}
              value={
                disableDateTime && timeValue
                  ? `${moment(disableDateTime).format(
                      "YYYY-MM-DD"
                    )}T${timeValue}`
                  : ""
              }
              onChange={(e) => {
                handleDisableModalDateTimeChange(
                    e,
                    showAlert,
                    setDisableDateTime,
                    setTimeValue,
                    setIsDirty
                )
              }}
              label="Date & Time to Remove APP Button from ACR"
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: moment().format("YYYY-MM-DDTHH:mm"),
                onKeyDown: (e) => e.preventDefault(),
              }}
              disabled={isOldPeoplePlannerDisabled}
            />
          }
        >
          {DISABLE_OLD_APP_DATE_AND_TIME_TOOLTIP_TEXT}
        </Tooltip>
        <Button
          className={styles.clearButton}
          disabled={!disableDateTime || !timeValue}
          onClick={() => {
            setDisableDateTime(null);
            setIsOldPeoplePlannerDisabled(false);
            setTimeValue(undefined);
            setIsDirty(true);
          }}
        >
          Clear Data
        </Button>
      </div>
    </Modal>
    </>
  );
};
